import { useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'

/**
 * GenderParityChart (Bare) - Chart only, no wrapper
 * For embedding in narrative HTML templates
 */
function GenderParityChart({ data }) {
  const genderStats = useMemo(() => {
    if (!data || typeof data !== 'object') return null

    const { boysCount = 0, girlsCount = 0, parityIndex = 1.0 } = data
    const total = boysCount + girlsCount

    const validParityIndex =
      typeof parityIndex === 'number' && !isNaN(parityIndex) ? parityIndex : 1.0

    return {
      boysCount,
      girlsCount,
      total,
      parityIndex: validParityIndex,
      boysPercentage: total > 0 ? ((boysCount / total) * 100).toFixed(1) : 0,
      girlsPercentage: total > 0 ? ((girlsCount / total) * 100).toFixed(1) : 0,
    }
  }, [data])

  const barChartOptions = useMemo(() => {
    if (!genderStats) return null

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: params => {
          const boys = params[0]
          const girls = params[1]
          return `<strong>Enrollment Comparison</strong><br/>
                  Boys: ${boys.value.toLocaleString()} (${genderStats.boysPercentage}%)<br/>
                  Girls: ${girls.value.toLocaleString()} (${genderStats.girlsPercentage}%)<br/>
                  Parity Index: ${genderStats.parityIndex.toFixed(2)}`
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: ['Enrollment'],
      },
      yAxis: {
        type: 'value',
        name: 'Number of Learners',
      },
      series: [
        {
          name: 'Boys',
          type: 'bar',
          data: [genderStats.boysCount],
          itemStyle: {
            color: '#3B82F6',
          },
          label: {
            show: true,
            position: 'top',
            formatter: params =>
              `${params.value.toLocaleString()} (${genderStats.boysPercentage}%)`,
          },
        },
        {
          name: 'Girls',
          type: 'bar',
          data: [genderStats.girlsCount],
          itemStyle: {
            color: '#EC4899',
          },
          label: {
            show: true,
            position: 'top',
            formatter: params =>
              `${params.value.toLocaleString()} (${genderStats.girlsPercentage}%)`,
          },
        },
      ],
    }
  }, [genderStats])

  if (!data || !genderStats || genderStats.total === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No gender parity data available
      </div>
    )
  }

  return <ReactECharts option={barChartOptions} style={{ height: '300px' }} />
}

GenderParityChart.propTypes = {
  data: PropTypes.shape({
    boysCount: PropTypes.number,
    girlsCount: PropTypes.number,
    parityIndex: PropTypes.number,
  }),
}

export default GenderParityChart
